<template>
    <div class="paper shadow-lg">
        <div class="d-flex">
            <div style="flex-basis: 0; flex-grow: 1">
                <IconBtn
                    v-if="!isHome()"
                    name="arrow-left"
                    class="closeBtnWrapper align-self-start"
                    size="lg"
                    style="float: left"
                    @click="() => changeState('HOME')"
                />
            </div>
            <div class="image-wrapper">
                <img :src="logo" class="image" />
            </div>
            <div style="flex-basis: 0; flex-grow: 1" class="justify-content-end">
                <IconBtn
                    v-if="isCompanyPlatform && showExitBtn()"
                    class="primary-outlined closeBtnWrapper"
                    size="lg"
                    style="float: right"
                    name="times"
                    @click="onCloseClick"
                />
            </div>
        </div>

        <div v-show="isLoading === false">
            <div v-if="isHome() || isLogIn() || isRegister() || isForgotPassword()">
                <b-collapse id="socialAuthCollapse" :visible="isHome()">
                    <h4 v-if="helperText()" class="subtitle text-center bold">
                        {{ helperText() }}
                    </h4>
                    <SocialAuthBtns
                        :showAll="showAllSocialAuthButtons"
                        @login="onLogIn"
                        @loadingChanged="value => (isLoading = value)"
                    />
                    <BaseButton
                        class="show-options-btn caption mute mt-3"
                        :class="{ 'mt-5': showAllSocialAuthButtons }"
                        tiny
                        style="font-size: 14px; display: block; margin: auto"
                        @click="toggleShowAllSocialAuthButtons"
                    >
                        {{
                            showAllSocialAuthButtons
                                ? $t('shared.loginModule.logInPaper.hideAllOptions')
                                : $t('shared.loginModule.logInPaper.showAllOptions')
                        }}
                    </BaseButton>
                    <b-collapse id="orUseEmailCollapse" :visible="!showAllSocialAuthButtons">
                        <div class="mute" style="margin-top: 20px">
                            {{ $t('shared.loginModule.logInPaper.useEmail') }}
                        </div>
                    </b-collapse>
                </b-collapse>

                <b-collapse
                    id="logInFormCollapse"
                    :visible="(isHome() || isLogIn()) && !showAllSocialAuthButtons"
                >
                    <LogInForm
                        ref="loginForm"
                        v-model="isLoading"
                        class="mt-4"
                        :isLogIn="isLogIn()"
                        @login="onLogIn"
                        @stateChange="changeState"
                    />
                </b-collapse>

                <b-collapse id="signUpCollapse" :visible="isHome() && !showAllSocialAuthButtons">
                    <div class="mt-2 d-flex align-items-center">
                        <p class="mb-0 mute">
                            {{ $t('shared.loginModule.logInPaper.noAccount') }}
                        </p>
                        <p class="mb-0 ml-2 link primary-color" @click="openRegister">
                            {{ $t('shared.loginModule.logInPaper.signUp') }}
                        </p>
                    </div>
                </b-collapse>

                <b-collapse id="registerFormCollapse" :visible="isRegister()">
                    <RegisterForm
                        ref="registerForm"
                        v-model="isLoading"
                        :state="state"
                        @register="onRegister"
                        @stateChange="changeState"
                    />
                </b-collapse>

                <b-collapse id="forgotPasswordCollapse" :visible="isForgotPassword()">
                    <ForgotPassword
                        ref="forgotPassword"
                        v-model="isLoading"
                        :state="state"
                        @stateChange="changeState"
                    />
                </b-collapse>
                <p v-safe-html="this.$t('shared.loginModule.tos')" class="mt-2 tos-styling" />
            </div>
        </div>

        <ProgressModule v-if="isLoading" />
    </div>
</template>

<script>
// Project components
import LOGO from '~/config/LOGO.png';

// Page specific components
import LogInForm from './LogInForm';
import RegisterForm from './RegisterForm';
import ForgotPassword from './ForgotPassword';
import ProgressModule from './ProgressModule';
import SocialAuthBtns from './SocialAuthBtns/SocialAuthBtns';

// Utils
import states from '../states';
import IntercomService from '~/services/IntercomService';
import BackgroundProcessesService from '~/services/BackgroundProcessesService';
import { isMobile } from '~/utils/windowUtils';
import localization from '../../../localization';
import UserConsentContext from '~/services/UserConsentContext';
import AuthService from '~/services/AuthService';

export default {
    components: {
        LogInForm,
        RegisterForm,
        ProgressModule,
        ForgotPassword,
        SocialAuthBtns,
    },
    data: () => ({
        logo: LOGO,
        state: states.HOME, // A string indicating which one section to render (Login, Register...etc)
        isLoading: false,

        registeredEmail: null, // The email that got registered
        showAllSocialAuthButtons: false,
        isCompanyPlatform: localization.isCompanyPlatform,
    }),
    methods: {
        helperText() {
            const helperText = this.$interfaces.logInModule.getHelperText();
            return helperText
                ? helperText
                : this.$interfaces.logInModule.getRedirect()
                ? this.$t('shared.loginModule.logInPaper.logInToAccess')
                : '';
        },
        showExitBtn() {
            return !this.$interfaces.logInModule.getDisabledCloseBtn();
        },
        isHome() {
            return this.state === states.HOME;
        },
        isLogIn() {
            return this.state === states.LOGIN;
        },
        isRegister() {
            return this.state === states.REGISTER;
        },
        isForgotPassword() {
            return this.state === states.FORGOT_PASSWORD;
        },
        changeState(newState) {
            this.$nextTick(() => {
                // Disable focus input on mobile devices :D
                if (isMobile()) {
                    return;
                }

                if (newState === states.LOGIN) {
                    this.$refs.loginForm.focusOnPassword();
                } else if (newState === states.REGISTER) {
                    this.$refs.registerForm.focusOnEmail();
                } else if (newState === states.HOME) {
                    this.$refs.loginForm.focusOnEmail();
                } else if (newState === states.FORGOT_PASSWORD) {
                    this.$refs.forgotPassword.focusOnEmail();
                }
            });
            this.state = newState;
        },
        async onLogIn() {
            const redirectURL = this.$interfaces.logInModule.getRedirect();
            this.$interfaces.logInModule.toggleOpen(); // Close login card
            this.$interfaces.logInModule.setRedirect(null);
            this.$interfaces.logInModule.setIsLoggedIn(true).then(() => {
                IntercomService.setUserData(IntercomService.EmailLogin);
            });
            AuthService.setIsLoggedIn(true); // Set the user as logged in

            // Set login state
            if (redirectURL) {
                this.$router.push(redirectURL); // Redirect to wanted path if configured
            } else {
                this.$router.go(); // Reload the page to update the content
            }
        },
        onRegister() {
            this.isLoading = false;
        },
        openRegister() {
            this.changeState(states.REGISTER);
        },
        onCloseClick() {
            this.$emit('close');

            if (!this.$interfaces.logInModule.isLoggedIn()) {
                BackgroundProcessesService.restartLogInPopup();
            }
        },
        toggleShowAllSocialAuthButtons() {
            this.showAllSocialAuthButtons = !this.showAllSocialAuthButtons;
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.$nextTick(() => {
                this.$refs.loginForm.focusOnEmail();
            });
        });
    },
    destroyed() {
        this.$interfaces.logInModule.setRedirect(null);
    },
};
</script>

<style scoped>
.closeBtnWrapper {
    color: var(--primary);
}
.backBtn {
    position: absolute;
    top: 10;
    left: 10;
}

.paper {
    display: block;
    position: relative;
    margin: 0 auto;

    width: 100%;
    max-width: 500px;

    border-radius: 4px;
    background-color: rgba(255, 255, 255, 1);

    padding: 60px 60px;
    overflow-y: auto;
}

.image-wrapper {
    width: 120px;
    height: 120px;
    max-width: 140px;
    max-height: 140px;
    overflow: hidden;

    display: block;
    margin: 0 auto;
}
.image {
    height: 100%;
    width: 100%;
    object-fit: scale-down;
}

.show-options-btn {
    transition: margin-top 0.3s;
}

@media only screen and (max-width: 600px) {
    .paper {
        margin: 0;
        padding: 18px 24px;
        width: 100vw;
        border-radius: 0;
        height: 100vh;
        padding-bottom: 20vh;
    }
    .image-wrapper {
        width: 100px;
        height: 100px;
        max-width: 100px;
        max-height: 100px;
    }
}

.tos-styling {
    font-size: 13px;
    color: #d3d3d3;
    margin: 0;
}
</style>
